// ============================================================================
// METRICS CONTEXT STRIP COMPONENT
//
// Consistent header bar shown at top of both Analytics and Business Metrics pages.
// Shows:
// - Active filters summary (date range, specialty, tenant)
// - Total runs in the period
// - "Last updated: Xm ago"
//
// Usage:
//   <MetricsContextStrip
//     filters={{ dateRange: 'Last 30 days', specialty: 'Cardiology' }}
//     totalRuns={142}
//     lastUpdatedAt={new Date()}
//   />
// ============================================================================

import React from 'react';
import { Clock, Filter } from 'lucide-react';

const formatLastUpdated = (date) => {
  if (!date) return null;
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const MetricsContextStrip = ({ filters = {}, totalRuns = 0, lastUpdatedAt = null }) => {
  // Build filter chips from the filters object
  const filterChips = Object.entries(filters)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => ({ key, value: String(value) }));

  const lastUpdatedStr = formatLastUpdated(lastUpdatedAt);

  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 mb-6 bg-slate-800/30 border border-slate-700/40 rounded-xl text-xs text-slate-400"
      role="status"
      aria-label="Metrics context"
    >
      {/* Filter chips */}
      {filterChips.length > 0 && (
        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
          {filterChips.map(({ key, value }) => (
            <span
              key={key}
              className="inline-flex items-center px-2 py-0.5 rounded bg-slate-700/50 text-slate-300"
            >
              {value}
            </span>
          ))}
        </div>
      )}

      {/* Total runs */}
      <span className="text-slate-400">
        {totalRuns.toLocaleString()} total runs
      </span>

      {/* Spacer */}
      <span className="flex-grow" />

      {/* Last updated */}
      {lastUpdatedStr && (
        <div className="flex items-center gap-1.5 text-slate-500">
          <Clock className="w-3.5 h-3.5" />
          <span>Updated {lastUpdatedStr}</span>
        </div>
      )}
    </div>
  );
};

export default MetricsContextStrip;
